import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  InputAdornment,
  Rating,
  Slide,
  AppBar,
  Toolbar,
  Autocomplete,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
} from '@mui/material';
import { Grid } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  VideoLibrary as VideoIcon,
  CameraAlt as CameraIcon,
  PhotoLibrary as GalleryIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSafeArea } from '../../hooks/useSafeArea';
import { useKeyboardScroll } from '../../hooks/useKeyboardScroll';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { Recipe, RecipeIngredient } from '../../services/recipesService';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface RecipesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (recipe: Omit<Recipe, 'id'>) => Promise<void>;
  onUpdate?: (recipeId: string, updates: Partial<Recipe>) => Promise<void>;
  onDelete?: (recipeId: string) => Promise<void>;
  recipe?: Recipe | null;
  mode: 'create' | 'edit';
}

const parseQuantity = (input: string | number): number => {
  if (typeof input === 'number') return input;
  if (!input) return 0;

  const cleanInput = input.trim().replace(',', '.');

  if (cleanInput.includes(' ')) {
    const parts = cleanInput.split(' ').filter(p => p);
    if (parts.length === 2) {
      const whole = parseFloat(parts[0]);
      if (parts[1].includes('/')) {
        const [num, den] = parts[1].split('/').map(parseFloat);
        if (!isNaN(whole) && !isNaN(num) && !isNaN(den) && den !== 0) {
          return whole + (num / den);
        }
      }
    }
  }

  if (cleanInput.includes('/')) {
    const [num, den] = cleanInput.split('/').map(parseFloat);
    if (!isNaN(num) && !isNaN(den) && den !== 0) {
      return num / den;
    }
  }

  return parseFloat(cleanInput) || 0;
};

const IngredientsList = React.memo(({
  ingredients,
  onChange,
  onAdd,
  onRemove,
  theme,
  units
}: {
  ingredients: RecipeIngredient[];
  onChange: (index: number, field: keyof RecipeIngredient, value: string | number) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  theme: any;
  units: string[];
}) => {
  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          color: theme.colors.text.primary,
          fontSize: '16px'
        }}
      >
        Ingredientes
      </Typography>
      {ingredients.map((ingredient, index) => (
        <Box key={index} sx={{ mb: 1 }}>
          <Grid container spacing={1} sx={{ mb: 0.5, alignItems: 'center' }}>
            <Grid size={{ xs: 5.5, sm: 5 }}>
              <TextField
                value={ingredient.name}
                onChange={(e) => onChange(index, 'name', e.target.value)}
                placeholder="Ingrediente"
                fullWidth
                size="small"
                slotProps={{
                  input: {
                    sx: { fontSize: '16px' }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 2, sm: 2.5 }}>
              <TextField
                type="text"
                value={ingredient.quantity}
                onChange={(e) => onChange(index, 'quantity', e.target.value)}
                placeholder="Qtd"
                fullWidth
                size="small"
                slotProps={{
                  input: {
                    sx: { fontSize: '16px' }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 3.5, sm: 3.5 }}>
              <Autocomplete
                value={ingredient.unit}
                onChange={(_, newValue) => {
                  onChange(index, 'unit', newValue || 'unidade');
                }}
                options={units}
                freeSolo
                fullWidth
                size="small"
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Unidade"
                    size="small"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        sx: { fontSize: '16px' }
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        paddingTop: '4px !important',
                        paddingBottom: '4px !important',
                      }
                    }}
                  />
                )}
                ListboxProps={{
                  sx: {
                    maxHeight: 200,
                    fontSize: '16px',
                    '& .MuiAutocomplete-option': {
                      fontSize: '16px',
                      py: 1,
                      px: 1.5,
                      minHeight: 'unset'
                    }
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 1, sm: 1 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {ingredients.length > 1 && (
                <IconButton
                  onClick={() => onRemove(index)}
                  size="small"
                  sx={{ color: '#d32f2f', p: 0.5 }}
                >
                  <DeleteIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Box>
      ))}
      <Button
        onClick={onAdd}
        variant="outlined"
        size="small"
        fullWidth
        sx={{
          borderColor: theme.colors.border,
          color: theme.colors.text.primary,
          '&:hover': { borderColor: theme.colors.primary },
        }}
      >
        + Adicionar Ingrediente
      </Button>
    </Box>
  );
});

export const RecipesDialog: React.FC<RecipesDialogProps> = ({
  open,
  onClose,
  onSave,
  onUpdate,
  onDelete,
  recipe,
  mode,
}) => {
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const safeArea = useSafeArea();
  useKeyboardScroll(); // Scroll automático quando teclado aparecer
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [showImageSourceDialog, setShowImageSourceDialog] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    ingredients: [{ name: '', quantity: '', unit: 'unidade' }] as RecipeIngredient[],
    instructions: '',
    servings: '4' as string | number,
    prepTime: '30' as string | number,
    description: '',
    category: '',
    imageUrl: '',
    videoUrl: '',
    rating: 0,
  });

  const [localTitle, setLocalTitle] = useState('');
  const [localInstructions, setLocalInstructions] = useState('');
  const [localDescription, setLocalDescription] = useState('');

  useEffect(() => {
    if (recipe && mode === 'edit') {
      setFormData({
        title: recipe.title || '',
        ingredients: recipe.ingredients.length > 0
          ? recipe.ingredients
          : [{ name: '', quantity: '', unit: 'unidade' }],
        instructions: recipe.instructions || '',
        servings: (recipe.servings && Number(recipe.servings)) || 4,
        prepTime: (recipe.prepTime && Number(recipe.prepTime)) || 30,
        description: recipe.description || '',
        category: recipe.category || '',
        imageUrl: recipe.imageUrl || '',
        videoUrl: recipe.videoUrl || '',
        rating: recipe.rating || 0,
      });
      setLocalTitle(recipe.title || '');
      setLocalInstructions(recipe.instructions || '');
      setLocalDescription(recipe.description || '');
      setImagePreview(recipe.imageUrl || null);
    } else {
      setFormData({
        title: '',
        ingredients: [{ name: '', quantity: '', unit: 'unidade' }],
        instructions: '',
        servings: '4',
        prepTime: '30',
        description: '',
        category: '',
        imageUrl: '',
        videoUrl: '',
        rating: 0,
      });
      setLocalTitle('');
      setLocalInstructions('');
      setLocalDescription('');
      setImagePreview(null);
    }
    setImageFile(null);
    setVideoFile(null);
  }, [recipe, mode, open]);

  const handleImageSource = async (source: CameraSource) => {
    try {
      setShowImageSourceDialog(false);

      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: source,
      });

      if (image.dataUrl) {
        setImagePreview(image.dataUrl);

        const response = await fetch(image.dataUrl);
        const blob = await response.blob();
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

        if (file.size > 5 * 1024 * 1024) {
          alert('A imagem deve ter no máximo 5MB');
          setImagePreview(null);
          setImageFile(null);
          return;
        }

        setImageFile(file);
      }
    } catch (error) {
      console.log('Usuário cancelou ou erro ao capturar imagem:', error);
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        alert('O vídeo deve ter no máximo 50MB');
        return;
      }

      if (!file.type.startsWith('video/')) {
        alert('Por favor, selecione um arquivo de vídeo válido');
        return;
      }

      setVideoFile(file);
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile || !user?.uid) return null;

    try {
      setUploadingImage(true);
      const timestamp = Date.now();
      const fileName = `${timestamp}_${imageFile.name}`;
      const storageRef = ref(storage, `recipes/${user.uid}/${fileName}`);

      await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(storageRef);

      return downloadURL;
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      alert('Erro ao fazer upload da imagem');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadVideo = async (): Promise<string | null> => {
    if (!videoFile || !user?.uid) return null;

    try {
      setUploadingVideo(true);
      const timestamp = Date.now();
      const fileName = `${timestamp}_${videoFile.name}`;
      const storageRef = ref(storage, `recipes/${user.uid}/videos/${fileName}`);

      await uploadBytes(storageRef, videoFile);
      const downloadURL = await getDownloadURL(storageRef);

      return downloadURL;
    } catch (error) {
      console.error('Erro ao fazer upload do vídeo:', error);
      alert('Erro ao fazer upload do vídeo');
      return null;
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    setFormData({ ...formData, imageUrl: '' });
  };

  const handleRemoveVideo = () => {
    setVideoFile(null);
    setFormData({ ...formData, videoUrl: '' });
  };

  const handleIngredientChange = React.useCallback((index: number, field: keyof RecipeIngredient, value: string | number) => {
    setFormData(prevData => {
      const newIngredients = [...prevData.ingredients];
      newIngredients[index] = {
        ...newIngredients[index],
        [field]: value
      };
      return { ...prevData, ingredients: newIngredients };
    });
  }, []);

  const handleAddIngredient = React.useCallback(() => {
    setFormData(prevData => ({
      ...prevData,
      ingredients: [...prevData.ingredients, { name: '', quantity: '', unit: 'unidade' }],
    }));
  }, []);

  const handleRemoveIngredient = React.useCallback((index: number) => {
    setFormData(prevData => {
      if (prevData.ingredients.length > 1) {
        return {
          ...prevData,
          ingredients: prevData.ingredients.filter((_, i) => i !== index)
        };
      }
      return prevData;
    });
  }, []);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Por favor, preencha o título da receita');
      return;
    }

    const validIngredients = formData.ingredients
      .filter(i => i.name.trim())
      .map(i => ({
        name: i.name.trim(),
        quantity: parseQuantity(i.quantity),
        unit: i.unit
      }));

    if (validIngredients.length === 0) {
      alert('Adicione pelo menos um ingrediente');
      return;
    }

    if (!formData.instructions.trim()) {
      alert('Por favor, preencha as instruções');
      return;
    }

    try {
      setLoading(true);

      let imageUrl = formData.imageUrl;
      let videoUrl = formData.videoUrl;

      if (imageFile) {
        const uploadedUrl = await uploadImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;

          if (mode === 'edit' && recipe?.imageUrl) {
            try {
              const oldImageRef = ref(storage, recipe.imageUrl);
              await deleteObject(oldImageRef);
            } catch (error) {
              console.log('Imagem antiga não encontrada');
            }
          }
        }
      }

      if (videoFile) {
        const uploadedVideoUrl = await uploadVideo();
        if (uploadedVideoUrl) {
          videoUrl = uploadedVideoUrl;

          if (mode === 'edit' && recipe?.videoUrl) {
            try {
              const oldVideoRef = ref(storage, recipe.videoUrl);
              await deleteObject(oldVideoRef);
            } catch (error) {
              console.log('Vídeo antigo não encontrado');
            }
          }
        }
      }

      const recipeData = {
        title: localTitle.trim(),
        ingredients: validIngredients,
        instructions: localInstructions.trim(),
        servings: typeof formData.servings === 'string' ? (parseInt(formData.servings) || 1) : formData.servings,
        prepTime: typeof formData.prepTime === 'string' ? (parseInt(formData.prepTime) || 30) : formData.prepTime,
        description: localDescription.trim(),
        imageUrl,
        videoUrl,
        rating: formData.rating,
        userId: user?.uid || '',
        createdAt: recipe?.createdAt || new Date(),
        updatedAt: new Date(),
      };

      if (mode === 'edit' && recipe && onUpdate) {
        await onUpdate(recipe.id, recipeData);
      } else {
        await onSave(recipeData);
      }

      onClose();
    } catch (error) {
      console.error('Erro ao salvar receita:', error);
      alert('Erro ao salvar receita');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!recipe || !onDelete) return;

    const confirmDelete = window.confirm('Tem certeza que deseja excluir esta receita?');
    if (!confirmDelete) return;

    try {
      setLoading(true);

      if (recipe.imageUrl) {
        try {
          const imageRef = ref(storage, recipe.imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.log('Erro ao deletar imagem:', error);
        }
      }

      if (recipe.videoUrl) {
        try {
          const videoRef = ref(storage, recipe.videoUrl);
          await deleteObject(videoRef);
        } catch (error) {
          console.log('Erro ao deletar vídeo:', error);
        }
      }

      await onDelete(recipe.id);
      onClose();
    } catch (error) {
      console.error('Erro ao excluir receita:', error);
      alert('Erro ao excluir receita');
    } finally {
      setLoading(false);
    }
  };

  const units = [
    'unidade',
    'g',
    'kg',
    'ml',
    'L',
    'xícara',
    'colher (sopa)',
    'colher (chá)',
    'colher (café)',
    'gotas',
    'pitada',
    'a gosto',
  ];

  const categories = [
    'Bolos',
    'Massas',
    'Risotos',
    'Carnes',
    'Peixes',
    'Aves',
    'Saladas',
    'Sopas',
    'Molhos',
    'Doces',
    'Bebidas',
    'Petiscos',
    'Pratos Vegetarianos',
    'Pratos Veganos',
    'Sem Glúten',
    'Outro',
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          backgroundColor: theme.colors.background,
        },
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.colors.card,
          paddingTop: `${safeArea.top}px`,
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 }, minHeight: { xs: 56, sm: 64 } }}>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon sx={{ color: theme.colors.text.primary }} />
          </IconButton>
          <Button
            startIcon={null}
            sx={{
              ml: 2,
              flex: 1,
              backgroundColor: theme.colors.primary,
              color: 'white',
              borderRadius: theme.radii.medium,
              padding: '8px 20px',
              fontSize: '0.875rem',
              fontWeight: 'normal',
              textTransform: 'none',
              maxWidth: 'fit-content',
              '&:hover': {
                backgroundColor: theme.colors.primary,
                opacity: 0.8,
              },
            }}
          >
            {mode === 'edit' ? 'Editar Receita' : 'Nova Receita'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {mode === 'edit' && onDelete && (
            <Button
              onClick={handleDelete}
              disabled={loading}
              sx={{ color: '#d32f2f' }}
            >
              Excluir
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={loading || uploadingImage || uploadingVideo}
            sx={{
              color: theme.colors.primary,
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: theme.colors.primary }} /> : mode === 'edit' ? 'Salvar' : 'Criar'}
          </Button>
        </Toolbar>
      </AppBar>

      {open && (
        <DialogContent
          sx={{
            pt: `${56 + safeArea.top + 16}px`,
            pb: `${400 + safeArea.bottom}px`,
            px: { xs: 2, sm: 3 },
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            '@supports (-webkit-touch-callout: none)': {
              pb: `${450 + safeArea.bottom}px`,
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 }, pt: 1 }}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: theme.colors.text.primary,
                  fontSize: '16px'
                }}
              >
                Foto da receita
              </Typography>
              <Box
                onClick={() => !imagePreview && !uploadingImage && setShowImageSourceDialog(true)}
                sx={{
                  width: '100%',
                  height: 200,
                  borderRadius: theme.radii.medium,
                  border: `2px dashed ${theme.colors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: theme.colors.card,
                  backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  cursor: !imagePreview && !uploadingImage ? 'pointer' : 'default',
                }}
              >
                {!imagePreview && !uploadingImage && (
                  <Box sx={{ textAlign: 'center' }}>
                    <UploadIcon sx={{ fontSize: '3rem', color: theme.colors.text.secondary, mb: 1 }} />
                    <Typography sx={{ color: theme.colors.text.secondary }}>
                      Clique para adicionar foto
                    </Typography>
                  </Box>
                )}
                {uploadingImage && <CircularProgress />}
                {imagePreview && (
                  <IconButton
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Box>

            <TextField
              label="Título da receita"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={() => setFormData(prev => ({ ...prev, title: localTitle }))}
              fullWidth
              required
              slotProps={{
                inputLabel: {
                  sx: { fontSize: '16px' }
                },
                input: {
                  sx: { fontSize: '16px' }
                }
              }}
            />

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 0.5,
                  color: theme.colors.text.primary,
                  fontSize: '16px'
                }}
              >
                Categoria
              </Typography>
              <Typography
                sx={{
                  mb: 1,
                  color: theme.colors.text.secondary,
                  fontSize: '13px'
                }}
              >
                Selecione uma categoria ou digite uma nova
              </Typography>
              <Autocomplete
                value={formData.category || null}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, category: newValue || '' });
                }}
                onInputChange={(_, newInputValue) => {
                  if (newInputValue !== formData.category) {
                    setFormData({ ...formData, category: newInputValue });
                  }
                }}
                options={categories}
                freeSolo
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Ex: Bolos, Massas, ou crie a sua"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        sx: { fontSize: '16px' },
                        endAdornment: (
                          <>
                            <InputAdornment position="end" sx={{ mr: 1 }}>
                              <AddIcon
                                sx={{
                                  color: theme.colors.primary,
                                  fontSize: 20,
                                  opacity: 0.7,
                                }}
                              />
                            </InputAdornment>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }
                    }}
                  />
                )}
                ListboxProps={{
                  sx: {
                    maxHeight: 250,
                    fontSize: '16px',
                    '& .MuiAutocomplete-option': {
                      fontSize: '16px',
                      py: 1.5,
                      px: 2,
                    }
                  }
                }}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    fontSize: '16px'
                  }
                }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="Porções"
                  value={String(formData.servings)}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, servings: val });
                  }}
                  onBlur={() => {
                    const val = String(formData.servings);
                    if (!val || val === '0' || val === '') {
                      setFormData({ ...formData, servings: '1' });
                    }
                  }}
                  fullWidth
                  slotProps={{
                    input: {
                      inputMode: 'numeric',
                      startAdornment: (
                        <InputAdornment position="start">
                          <GroupIcon sx={{ color: theme.colors.text.secondary }} />
                        </InputAdornment>
                      ),
                      sx: { fontSize: '16px' }
                    },
                    inputLabel: {
                      sx: { fontSize: '16px' }
                    }
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Tempo (min)"
                  value={String(formData.prepTime)}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setFormData({ ...formData, prepTime: val });
                  }}
                  onBlur={() => {
                    const val = String(formData.prepTime);
                    if (!val || val === '0' || val === '') {
                      setFormData({ ...formData, prepTime: '30' });
                    }
                  }}
                  fullWidth
                  slotProps={{
                    input: {
                      inputMode: 'numeric',
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeIcon sx={{ color: theme.colors.text.secondary }} />
                        </InputAdornment>
                      ),
                      sx: { fontSize: '16px' }
                    },
                    inputLabel: {
                      sx: { fontSize: '16px' }
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: theme.colors.text.primary,
                  fontSize: '16px'
                }}
              >
                Classificação
              </Typography>
              <Rating
                value={formData.rating}
                onChange={(_, newValue) => setFormData({ ...formData, rating: newValue || 0 })}
                precision={0.5}
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#d9a520',
                  },
                  '& .MuiRating-icon': {
                    fontSize: { xs: '1.8rem', sm: '2rem' }
                  }
                }}
              />
            </Box>

            <IngredientsList
              ingredients={formData.ingredients}
              onChange={handleIngredientChange}
              onAdd={handleAddIngredient}
              onRemove={handleRemoveIngredient}
              theme={theme}
              units={units}
            />

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: theme.colors.text.primary,
                  fontSize: '16px'
                }}
              >
                Modo de preparo *
              </Typography>
              <TextField
                placeholder="Descreva o passo a passo..."
                value={localInstructions}
                onChange={(e) => setLocalInstructions(e.target.value)}
                onBlur={() => setFormData(prev => ({ ...prev, instructions: localInstructions }))}
                multiline
                rows={4}
                fullWidth
                slotProps={{
                  input: {
                    sx: {
                      fontSize: '16px',
                      '& textarea': {
                        WebkitOverflowScrolling: 'touch',
                      }
                    }
                  }
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: theme.colors.text.primary,
                  fontSize: '16px'
                }}
              >
                Vídeo da receita (opcional)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                fullWidth
                startIcon={uploadingVideo ? <CircularProgress size={20} /> : <VideoIcon />}
                disabled={uploadingVideo}
                sx={{
                  borderColor: theme.colors.border,
                  color: theme.colors.text.primary,
                  justifyContent: 'flex-start',
                  '&:hover': { borderColor: theme.colors.primary },
                }}
              >
                {videoFile ? videoFile.name : formData.videoUrl ? 'Vídeo adicionado' : 'Adicionar vídeo'}
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoChange}
                  hidden
                />
              </Button>
              {(videoFile || formData.videoUrl) && (
                <Button
                  onClick={handleRemoveVideo}
                  size="small"
                  sx={{ mt: 1, color: '#d32f2f' }}
                >
                  Remover vídeo
                </Button>
              )}
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: theme.colors.text.primary,
                  fontSize: '16px'
                }}
              >
                Observações
              </Typography>
              <TextField
                placeholder="Anotações livres sobre a receita..."
                value={localDescription}
                onChange={(e) => setLocalDescription(e.target.value)}
                onBlur={() => setFormData(prev => ({ ...prev, description: localDescription }))}
                multiline
                rows={2}
                fullWidth
                slotProps={{
                  input: {
                    sx: { fontSize: '16px' }
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      )}

      <Drawer
        anchor="bottom"
        open={showImageSourceDialog}
        onClose={() => setShowImageSourceDialog(false)}
        sx={{
          zIndex: 1500, // Higher than Dialog's default z-index (1300)
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }
        }}
      >
        <Box sx={{ p: 2, pb: `${safeArea.bottom + 16}px` }}>
          <Box sx={{
            backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: theme.radii.large,
            overflow: 'hidden',
            mb: 1,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.colors.border}`, display: 'flex', justifyContent: 'center' }}>
              <Typography variant="subtitle2" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
                Adicionar Foto
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              <ListItem
                onClick={() => handleImageSource(CameraSource.Camera)}
                sx={{
                  cursor: 'pointer',
                  py: 2,
                  borderBottom: `1px solid ${theme.colors.border}`,
                  '&:active': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.colors.primary }}>
                  <CameraIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Tirar Foto"
                  primaryTypographyProps={{
                    sx: {
                      color: theme.colors.primary,
                      fontWeight: 600,
                      fontSize: '17px',
                      textAlign: 'center',
                      ml: -5 // Compensate Icon
                    }
                  }}
                />
              </ListItem>
              <ListItem
                onClick={() => handleImageSource(CameraSource.Photos)}
                sx={{
                  cursor: 'pointer',
                  py: 2,
                  '&:active': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.colors.primary }}>
                  <GalleryIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Escolher da Galeria"
                  primaryTypographyProps={{
                    sx: {
                      color: theme.colors.primary,
                      fontWeight: 600,
                      fontSize: '17px',
                      textAlign: 'center',
                      ml: -5
                    }
                  }}
                />
              </ListItem>
            </List>
          </Box>

          <Button
            fullWidth
            onClick={() => setShowImageSourceDialog(false)}
            sx={{
              backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: theme.radii.large,
              py: 2,
              color: '#007AFF', // iOS blue
              fontWeight: 600,
              fontSize: '17px',
              textTransform: 'none',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(40, 40, 40, 0.95)' : 'rgba(245, 245, 245, 0.95)',
              }
            }}
          >
            Cancelar
          </Button>
        </Box>
      </Drawer>
    </Dialog>
  );
};
